import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Bot, Sparkles, TrendingUp, TrendingDown, AlertTriangle, Zap, RefreshCw, Play, Square, Activity, Check, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export default function AITradingPage() {
  const { user } = useAuth();
  const { formatCurrency, preferredCurrency } = useCurrency();
  const [settings, setSettings] = useState({ enabled: false, risk_level: 'medium', max_trade_amount: 100, assets: [] });
  const [tradeLogs, setTradeLogs] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoTradingStatus, setAutoTradingStatus] = useState({ running: false });
  const [aiEnabled, setAiEnabled] = useState(false);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchTradeLogs, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, logsRes, statusRes] = await Promise.all([
        api.get('/trading/settings'),
        api.get('/trading/logs'),
        api.get('/trading/auto-trading-status')
      ]);
      setSettings(settingsRes.data);
      setTradeLogs(logsRes.data);
      setAutoTradingStatus(statusRes.data);
      setAiEnabled(settingsRes.data?.enabled || false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTradeLogs = async () => {
    try {
      const logsRes = await api.get('/trading/logs');
      setTradeLogs(logsRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.post('/trading/settings', settings);
      toast.success('AI Trading settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAI = async (enabled) => {
    try {
      setAiEnabled(enabled);
      await api.post('/trading/settings', { ...settings, enabled });
      toast.success(enabled ? 'AI Trading enabled! Trading every 30 minutes.' : 'AI Trading disabled');
      setSettings({ ...settings, enabled });
    } catch (error) {
      toast.error('Failed to update AI trading');
      setAiEnabled(!enabled);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post('/trading/analyze');
      setAnalysis(res.data);
      toast.success('AI analysis complete');
    } catch (error) {
      toast.error('Analysis failed - try again');
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <RefreshCw className="w-8 h-8 text-[#00E676] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ai-trading-page">
        {/* Live Banner */}
        <div className="p-4 rounded-xl bg-gradient-to-r from-[#00E676]/20 to-[#00E676]/10 border border-[#00E676]/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#00E676]/20 flex items-center justify-center flex-shrink-0">
              <Bot className="w-6 h-6 text-[#00E676]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-white">{(autoTradingStatus?.running && aiEnabled) ? 'AI Trading is LIVE' : 'AI Trading is OFF'}</h3>
                <span className={(autoTradingStatus?.running && aiEnabled) ? "px-2 py-0.5 text-xs rounded-full bg-[#00E676]/20 text-[#00E676] flex items-center gap-1" : "px-2 py-0.5 text-xs rounded-full bg-gray-600/20 text-gray-400 flex items-center gap-1"}>
          <span className={(autoTradingStatus?.running && aiEnabled) ? "w-2 h-2 rounded-full bg-[#00E676] animate-pulse" : "w-2 h-2 rounded-full bg-gray-500"}></span>
                  {(autoTradingStatus?.running && aiEnabled) ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
              <p className="text-sm text-gray-400 mt-1">
                {(autoTradingStatus?.running && aiEnabled) ? 'AI trades every 30 minutes with target returns of 15-25% daily.' : 'Auto-trading is currently off — no trades are being placed.'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              AI Trading
            </h1>
            <p className="text-gray-400 mt-1">Automated trading powered by AI - Target: 15-25% daily returns</p>
          </div>
          <Button 
            onClick={runAnalysis} 
            disabled={analyzing}
            className="btn-primary gap-2" 
            data-testid="analyze-btn"
          >
            {analyzing ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {analyzing ? 'Analyzing...' : 'Run AI Analysis'}
          </Button>
        </div>

        {/* AI Trading Toggle Card */}
        <Card className="card-dashboard border-2 border-[#00E676]/30">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${aiEnabled ? 'bg-[#00E676]/20' : 'bg-white/10'}`}>
                  <Bot className={`w-8 h-8 ${aiEnabled ? 'text-[#00E676]' : 'text-gray-500'}`} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    AI Auto-Trading
                  </h2>
                  <p className={`text-sm ${aiEnabled ? 'text-[#00E676]' : 'text-gray-400'}`}>
                    {aiEnabled ? 'Active - Trading every 30 minutes' : 'Disabled - Enable to start automated trading'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={`text-sm font-medium ${aiEnabled ? 'text-[#00E676]' : 'text-gray-500'}`}>
                  {aiEnabled ? 'ON' : 'OFF'}
                </span>
                <Switch
                  checked={aiEnabled}
                  onCheckedChange={handleToggleAI}
                  data-testid="ai-toggle"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Platform Auto Trading Status */}
        <Card className="card-dashboard">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-[#F7931A]/20">
                  <Activity className="w-8 h-8 text-[#F7931A]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Platform AI Trading Engine
                  </h2>
                  <p className="text-sm text-[#00E676]">
                    <span className="inline-flex items-center gap-1">
            <span className={autoTradingStatus?.running ? "w-2 h-2 rounded-full bg-[#00E676] animate-pulse" : "w-2 h-2 rounded-full bg-gray-500"}></span>
                      {autoTradingStatus?.running ? 'Running - Next trade cycle in ~30 minutes' : 'Not running'}
                    </span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">Target Daily Return</p>
                <p className="text-2xl font-bold text-[#00E676]">15-25%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Settings */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Trading Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm text-gray-400">Risk Level</label>
                <Select value={settings.risk_level} onValueChange={(v) => setSettings({ ...settings, risk_level: v })}>
                  <SelectTrigger className="input-dark" data-testid="risk-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low Risk (5-10% daily target)</SelectItem>
                    <SelectItem value="medium">Medium Risk (15-20% daily target)</SelectItem>
                    <SelectItem value="high">High Risk (20-25% daily target)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-gray-400">Max Trade Amount per Cycle</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="number"
                    value={settings.max_trade_amount}
                    onChange={(e) => setSettings({ ...settings, max_trade_amount: parseInt(e.target.value) || 0 })}
                    className="input-dark pl-10"
                    data-testid="max-trade-input"
                  />
                </div>
              </div>
              <Button onClick={handleSaveSettings} disabled={saving} className="w-full btn-primary" data-testid="save-settings-btn">
                {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </CardContent>
          </Card>

          {/* AI Analysis Results */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>AI Market Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              {analysis ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-white/5">
                    <p className="text-sm text-gray-400">Market Sentiment</p>
                    <p className={`text-xl font-bold ${analysis.sentiment === 'bullish' ? 'text-[#00E676]' : analysis.sentiment === 'bearish' ? 'text-red-400' : 'text-yellow-500'}`}>
                      {analysis.sentiment?.toUpperCase() || 'NEUTRAL'}
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5">
                    <p className="text-sm text-gray-400">Recommendation</p>
                    <p className="text-white">{analysis.recommendation || 'Hold current positions'}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5">
                    <p className="text-sm text-gray-400">Confidence Score</p>
                    <p className="text-xl font-bold text-[#00E676]">{analysis.confidence || 85}%</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Bot className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Click "Run AI Analysis" to get market insights</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Trade Logs */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Activity className="w-5 h-5 text-[#00E676]" />
              Recent AI Trades
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tradeLogs.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {tradeLogs.slice(0, 20).map((log, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${log.action === 'BUY' ? 'bg-[#00E676]/20' : 'bg-red-500/20'}`}>
                        {log.action === 'BUY' ? (
                          <TrendingUp className="w-4 h-4 text-[#00E676]" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">{log.action} {log.symbol || log.asset}</p>
                        <p className="text-xs text-gray-500">{new Date(log.timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-white">${log.amount?.toFixed(2) || '0.00'}</p>
                      {log.profit && (
                        <p className={`text-xs ${log.profit >= 0 ? 'text-[#00E676]' : 'text-red-400'}`}>
                          {log.profit >= 0 ? '+' : ''}{log.profit.toFixed(2)}%
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No trade logs yet. AI trades every 30 minutes.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
